import {
  Button,
  Flex,
  Popconfirm,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useAuth } from "../../providers/AuthProvider";
import { useLogs } from "../../providers/LogProvider";
import {
  getS3BackupConfigs,
  createS3BackupConfig,
  updateS3BackupConfig,
  deleteS3BackupConfig,
  resumeS3BackupConfig,
} from "../../api/backups";
import { S3BackupConfig, CreateS3BackupConfigRequest } from "../../api/types";
import { ConfigForm } from "./ConfigForm";
import { SegmentsDrawer } from "./SegmentsDrawer";
import { notification } from "antd";

const { Title } = Typography;

const statusColor: Record<string, string> = {
  active: "green",
  paused: "warning",
  error: "red",
};

export const Backups = () => {
  const { token } = useAuth();
  const { apps } = useLogs();

  const [configs, setConfigs] = useState<S3BackupConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [editingConfig, setEditingConfig] = useState<S3BackupConfig | null>(
    null,
  );
  const [segmentsConfigId, setSegmentsConfigId] = useState<string | null>(null);
  const [segmentsConfigName, setSegmentsConfigName] = useState("");

  const fetchConfigs = async () => {
    if (!token) return;
    setLoading(true);
    const res = await getS3BackupConfigs(token);
    setConfigs(res?.configs ?? []);
    setLoading(false);
  };

  useEffect(() => {
    fetchConfigs();
  }, []);

  const handleOpenCreate = () => {
    setEditingConfig(null);
    setFormOpen(true);
  };

  const handleOpenEdit = (config: S3BackupConfig) => {
    setEditingConfig(config);
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setEditingConfig(null);
  };

  const handleFormSubmit = async (values: CreateS3BackupConfigRequest) => {
    if (!token) return;
    setFormLoading(true);
    let res;
    if (editingConfig) {
      res = await updateS3BackupConfig(token, editingConfig.id, values);
    } else {
      res = await createS3BackupConfig(token, values);
    }
    setFormLoading(false);
    if (res?.success) {
      notification.success({
        message: "Success",
        description: editingConfig
          ? "Backup config updated"
          : "Backup config created",
      });
      handleFormClose();
      fetchConfigs();
    }
  };

  const handleDelete = async (id: string) => {
    if (!token) return;
    const res = await deleteS3BackupConfig(token, id);
    if (res?.success) {
      notification.success({
        message: "Success",
        description: "Config deleted",
      });
      setConfigs((prev) => prev.filter((c) => c.id !== id));
    }
  };

  const handleResume = async (id: string) => {
    if (!token) return;
    const res = await resumeS3BackupConfig(token, id);
    if (res?.success) {
      notification.success({
        message: "Success",
        description: "Config resumed",
      });
      fetchConfigs();
    }
  };

  const handleViewSegments = (config: S3BackupConfig) => {
    setSegmentsConfigId(config.id);
    setSegmentsConfigName(config.name);
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <Tag color={statusColor[status] ?? "default"}>{status}</Tag>
      ),
    },
    {
      title: "Bucket",
      dataIndex: "s3Bucket",
      key: "s3Bucket",
    },
    {
      title: "Endpoint",
      dataIndex: "s3Endpoint",
      key: "s3Endpoint",
      render: (endpoint: string) => (
        <Typography.Text style={{ fontSize: 12 }}>{endpoint}</Typography.Text>
      ),
    },
    {
      title: "Apps",
      dataIndex: "appIds",
      key: "appIds",
      render: (appIds: string[] | null) => {
        if (!appIds || appIds.length === 0) return <em>all</em>;
        const names = appIds.map((id) => {
          const app = apps?.find((a) => a.id === id);
          return app?.name ?? id;
        });
        return names.join(", ");
      },
    },
    {
      title: "Expiration",
      dataIndex: "expirationDays",
      key: "expirationDays",
      render: (days: number) => (days === 0 ? "never" : `${days} days`),
    },
    {
      title: "Last Upload",
      dataIndex: "lastUploadAt",
      key: "lastUploadAt",
      render: (d: string | null) => (d ? new Date(d).toLocaleString() : "-"),
    },
    {
      title: "Error",
      dataIndex: "errorReason",
      key: "errorReason",
      render: (reason: string | null) =>
        reason ? (
          <Tooltip title={reason}>
            <Typography.Text type="danger" style={{ fontSize: 12 }}>
              {reason.length > 40 ? reason.slice(0, 40) + "…" : reason}
            </Typography.Text>
          </Tooltip>
        ) : null,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: unknown, record: S3BackupConfig) => (
        <Flex gap={8}>
          <Tooltip title="View segments">
            <Button
              size="small"
              icon={<UnorderedListOutlined />}
              onClick={() => handleViewSegments(record)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleOpenEdit(record)}
            />
          </Tooltip>
          {(record.status === "paused" || record.status === "error") && (
            <Tooltip title="Resume">
              <Button
                size="small"
                icon={<PlayCircleOutlined />}
                onClick={() => handleResume(record.id)}
              />
            </Tooltip>
          )}
          <Popconfirm
            title="Delete this backup config?"
            description="This cannot be undone."
            onConfirm={() => handleDelete(record.id)}
            okText="Delete"
            okType="danger"
          >
            <Tooltip title="Delete">
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Flex>
      ),
    },
  ];

  return (
    <Flex vertical style={{ padding: "0 8px" }}>
      <Flex
        justify="space-between"
        align="center"
        style={{ marginBottom: 20, marginTop: 20 }}
      >
        <Title level={4} style={{ margin: 0 }}>
          S3 Backups
        </Title>
        <Button type="primary" onClick={handleOpenCreate}>
          Create config
        </Button>
      </Flex>

      <Table
        dataSource={configs}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={false}
        scroll={{ x: true }}
      />

      <ConfigForm
        open={formOpen}
        onClose={handleFormClose}
        onSubmit={handleFormSubmit}
        loading={formLoading}
        initialValues={editingConfig}
        apps={apps ?? []}
      />

      <SegmentsDrawer
        configId={segmentsConfigId}
        configName={segmentsConfigName}
        onClose={() => setSegmentsConfigId(null)}
      />
    </Flex>
  );
};

export default Backups;
